// =============================================================================
// QuickBooks (Intuit) — connection state + method declarations
//
// PULL-ONLY, by accounting's ruling ("Push and pull?" → "no, we can't"): this integration reads
// Bills/Payments to reconcile against the PM cash ledger and NEVER writes back to QuickBooks. There is
// deliberately no push surface here to grow one from.
//
// Step 1 (this file): OAuth2 connect + token storage. The pull itself lands on top of this.
// =============================================================================

import { Mongo } from 'meteor/mongo';
import { Doc } from '/imports/types';
import { DeclareMethod } from '/imports/methods';

// --- One connected QuickBooks company per org. Intuit scopes every API call to a `realmId` (their id
//     for the company file), so the realm — not the token — is the identity that matters. ---
export interface QuickbooksConnection extends Doc {
  orgId?: string;
  realmId: string;              // Intuit company id; every API path is /company/{realmId}/...
  environment?: 'sandbox' | 'production'; // the Intuit host the tokens were minted on (unset on early connections)
  companyName?: string;         // resolved on connect, so the UI can name what is attached
  accessToken: string;
  refreshToken: string;
  accessExpiresAt: Date;
  // Intuit's refresh token ROTATES on each use and dies after ~100 days idle, so a connection can go
  // stale without anyone touching it. Surfaced so the UI can say "reconnect" rather than fail opaquely.
  refreshExpiresAt?: Date;
  // Set when Intuit rejects our refresh token outright (revoked in the QuickBooks UI, or rotation
  // desynced). Distinct from refreshExpiresAt lapsing: the clock can look fine and the grant still be
  // dead, and only a fresh consent fixes it — retrying the refresh never will.
  needsReconnect?: boolean;
  connectedByUserId?: string;
  connectedByName?: string;
  lastPulledAt?: Date;
  // When the refresh token was last exercised. Intuit expires it on an IDLE clock, so this — not
  // createdAt, and not updatedAt — is what says how close a connection is to dying of neglect.
  refreshedAt?: Date;
}

export const QuickbooksConnections = new Mongo.Collection<QuickbooksConnection>('quickbooksConnections');

// --- A pending OAuth attempt. Persisted rather than held in memory for two reasons: a server restart
//     between consent and callback would otherwise invalidate the attempt, and a SHAREABLE link (one an
//     admin emails to their accountant) has to outlive the session that minted it. Single-use either
//     way — the callback deletes the row it consumes. ---
export interface QuickbooksConnectState extends Doc {
  _id: string;               // the state nonce itself
  userId: string;            // who minted it; the callback has no session of its own to read
  orgId?: string;            // the org the resulting connection belongs to
  expiresAt: Date;
  shareable?: boolean;       // minted for emailing, so it carries the long TTL
}

export const QuickbooksConnectStates = new Mongo.Collection<QuickbooksConnectState>('quickbooksConnectStates');

/** What the UI needs to render the connection card — never the tokens themselves. */
export interface QuickbooksStatus {
  connected: boolean;
  realmId?: string;
  companyName?: string;
  environment?: 'sandbox' | 'production';
  connectedByName?: string;
  connectedAt?: Date;
  lastPulledAt?: Date;
  expired?: boolean;            // refresh token lapsed — needs a fresh consent, not a refresh
  needsReconnect?: boolean;     // grant revoked by Intuit — same remedy, different cause
  refreshedAt?: Date;
  accessExpiresAt?: Date;
  refreshExpiresAt?: Date;
}

/** Builds the Intuit consent URL (state is a one-time nonce). `shareable` mints a long-lived link an
 *  admin can send to whoever actually holds the QuickBooks login. */
export const quickbooksAuthUrl = DeclareMethod<{ shareable?: boolean } | undefined, string>('quickbooksAuthUrl');
export const quickbooksStatus = DeclareMethod<undefined, QuickbooksStatus>('quickbooksStatus');
export const quickbooksDisconnect = DeclareMethod<undefined, void>('quickbooksDisconnect');

/** What a hand-triggered refresh reports back. */
export interface QuickbooksRefreshResult {
  ok: boolean;
  accessExpiresAt?: Date;
  refreshExpiresAt?: Date;
  message?: string;
}

/** Forces a token refresh for the org's connection, whatever the access token's remaining life. */
export const quickbooksRefreshNow = DeclareMethod<undefined, QuickbooksRefreshResult>('quickbooksRefreshNow');

// --- API explorer -----------------------------------------------------------------------------------
// A super-user tool for poking at the Accounting API by hand: pick an endpoint, send, read the response.
// GET only, which is what keeps it inside the pull-only rule at the top of this file — every QuickBooks
// write is a POST, so a tool that cannot POST cannot write, whatever path is typed into it.

/** One preset in the explorer's endpoint list. `path` is relative to `company/{realmId}/`. */
export interface QuickbooksEndpoint {
  key: string;
  label: string;
  path: string;
  params?: string;
}

// Curated around the cash reconciliation this integration exists for, plus the two calls worth having
// when a connection misbehaves: Company Info proves the token and realm agree, and CDC is how Intuit
// expects an integration to poll for changes rather than re-reading everything.
export const quickbooksEndpoints: QuickbooksEndpoint[] = [
  { 'key': 'companyInfo', 'label': 'Company Info', 'path': 'companyinfo/{realmId}' },
  { 'key': 'preferences', 'label': 'Preferences', 'path': 'preferences' },
  { 'key': 'bills', 'label': 'Bills', 'path': 'query', 'params': 'query=select * from Bill maxresults 20' },
  { 'key': 'billPayments', 'label': 'Bill Payments', 'path': 'query', 'params': 'query=select * from BillPayment maxresults 20' },
  { 'key': 'payments', 'label': 'Payments', 'path': 'query', 'params': 'query=select * from Payment maxresults 20' },
  { 'key': 'invoices', 'label': 'Invoices', 'path': 'query', 'params': 'query=select * from Invoice maxresults 20' },
  { 'key': 'purchases', 'label': 'Purchases', 'path': 'query', 'params': 'query=select * from Purchase maxresults 20' },
  { 'key': 'vendors', 'label': 'Vendors', 'path': 'query', 'params': 'query=select * from Vendor maxresults 20' },
  { 'key': 'customers', 'label': 'Customers', 'path': 'query', 'params': 'query=select * from Customer maxresults 20' },
  { 'key': 'accounts', 'label': 'Accounts', 'path': 'query', 'params': 'query=select * from Account maxresults 20' },
  { 'key': 'attachables', 'label': 'Attachables', 'path': 'query', 'params': 'query=select * from Attachable maxresults 20' },
  { 'key': 'cdc', 'label': 'Change Data Capture', 'path': 'cdc', 'params': 'entities=Bill,BillPayment,Payment&changedSince=2026-01-01' },
  { 'key': 'profitAndLoss', 'label': 'Report: Profit and Loss', 'path': 'reports/ProfitAndLoss' },
  { 'key': 'balanceSheet', 'label': 'Report: Balance Sheet', 'path': 'reports/BalanceSheet' },
];

export interface QuickbooksApiCallArg {
  path: string;
  params?: string;
  // Leave blank to borrow the org's stored connection, which also fixes the realm and environment to
  // whatever that connection is. A pasted token is used for the one call and never persisted server
  // side — the explorer does keep it in the browser between reloads, which is the only copy of it.
  accessToken?: string;
  realmId?: string;
  environment?: 'sandbox' | 'production';
  minorVersion?: string;
}

/** Everything the explorer needs to show, including for a call Intuit rejected. */
export interface QuickbooksApiCallResult {
  ok: boolean;
  status?: number;
  url: string;                  // echoed back so what was actually called is never in doubt
  environment: 'sandbox' | 'production';
  realmId: string;
  tid?: string;                 // intuit_tid — Intuit support cannot look a call up without it
  durationMs: number;
  body?: any;
  message?: string;
}

/** Sends one GET to the Accounting API and returns the raw response. Super-user only. */
export const quickbooksApiCall = DeclareMethod<QuickbooksApiCallArg, QuickbooksApiCallResult>('quickbooksApiCall');

// --- Cash sync -------------------------------------------------------------------------------------
// Pulls Invoices and their Payments onto the inbound cash events behind the Budget tab. Inflows only;
// see server/integrations/quickbooksCash.ts for why outflows are not here yet.

export interface QuickbooksCashSyncArgs {
  dryRun?: boolean;
  // A pasted token, for verifying a sweep against production books from a server configured for sandbox.
  accessToken?: string;
  realmId?: string;
  environment?: 'sandbox' | 'production';
  // Only used by the local-dev outflow mock: the project to seed mock suggestions for first (the Budget
  // tab the button was clicked from), so they appear where you are looking.
  projectId?: string;
}

/** Runs one inflow sweep. Super-user only; `dryRun` reports what it would write without writing. */
export const quickbooksCashSync = DeclareMethod<QuickbooksCashSyncArgs, any>('quickbooksCashSync');

/** Runs one OUTFLOW (Bills -> Uses) sweep. Super-user only; `dryRun` reports without writing. */
export const quickbooksOutflowSync = DeclareMethod<QuickbooksCashSyncArgs, any>('quickbooksOutflowSync');

/** Confirms a pending Bill->Use suggestion, writing its amount onto the milestone's Use.
 *  For a Tier 3 (unassigned) suggestion the caller supplies the milestoneKey to place it. */
export const quickbooksApplyCashSuggestion = DeclareMethod<{ suggestionId: string, milestoneKey?: string }, void>('quickbooksApplyCashSuggestion');

/** Dismisses a pending Bill->Use suggestion without writing anything. */
export const quickbooksDismissCashSuggestion = DeclareMethod<{ suggestionId: string }, void>('quickbooksDismissCashSuggestion');
